import Carousel from '@components/carousel/dynamicCarousel';
import { map, mergeMap, of } from 'rxjs';
import onArticleCardCarouselSettingsReady$ from './articleCardCarouselSettings';
import ArticleCardCarousel  from './articleCardCarousel';
import getArticleCards$ from '../apis/getArticleCards';

/**
 * Observable that emits the HTML content for an article card carousel once the settings are ready.
 * 
 * This observable listens for the `onArticleCardCarouselSettingsReady$` event, processes the settings,
 * and retrieves the article search results if a search command is provided. If no search command is
 * specified, it emits an empty string for the article cards HTML.
 * 
 * @constant
 * @type {Observable<{ articleCardsHtml: string, carouselQuerySelector: string }>}
 * 
 * @emits {Object} - An object containing:
 *   - `articleCardsHtml` {string}: The HTML content for the article cards.
 *   - `carouselQuerySelector` {string}: The query selector for the carousel element.
 */
const onArticleCardCarouselReady$ = onArticleCardCarouselSettingsReady$.pipe(
  mergeMap((settings) => {
    const articlesSearchCommand = settings.articlesSearchCommand;
    const carouselQuerySelector = settings.carouselQuerySelector;

    // If no search command is provided, emit an empty string
    if (!articlesSearchCommand) {
      return of({ articleCardsHtml: '', carouselQuerySelector });
    }

    return getArticleCards$(articlesSearchCommand).pipe(
      map((articleCardsHtml) => ({ articleCardsHtml, carouselQuerySelector }))
    );
  })
);

/**
 * Updates the article card carousel by initializing it with the provided HTML and query selector.
 *
 * @param {Object} params - The parameters for updating the carousel.
 * @param {string} params.carouselQuerySelector - The CSS query selector for the carousel container.
 * @param {string} params.articleCardsHtml The HTML string representing the article cards to be displayed.
 */
const updateArticleCardCarousel = function ({carouselQuerySelector, articleCardsHtml}) {
  const articleCardCarousel = new ArticleCardCarousel(articleCardsHtml, carouselQuerySelector);
  articleCardCarousel.setHtml();
};

/**
 * Initializes and configures a carousel with the provided settings.
 *
 * This function creates a new instance of the `Carousel` class, applies the provided settings,
 * and initializes the carousel.
 *
 * @param {Object} settings - The configuration settings for the carousel.
 * @param {string} settings.itemListQuerySelector - The query selector for the list of items in the carousel.
 * @param {string} settings.previousQuerySelector - The query selector for the "previous" navigation button.
 * @param {string} settings.nextQuerySelector - The query selector for the "next" navigation button.
 *
 * @example
 * const settings = {
 *   itemListQuerySelector: '[data-carousel="carousel1"] [data-item-list]',
 *   previousQuerySelector: '[data-carousel="carousel1"] [data-left-arrow]',
 *   nextQuerySelector: '[data-carousel="carousel1"] [data-right-arrow]',
 * };
 * setArticleCardCarousel(settings);
 */
const setArticleCardCarousel = function (settings) {
  const { carouselQuerySelector } = settings
  const carouselSettings = {
    itemListQuerySelector: `${carouselQuerySelector} [data-item-list]`,
    previousQuerySelector: `${carouselQuerySelector} [data-left-arrow]`,
    nextQuerySelector: `${carouselQuerySelector} [data-right-arrow]`,
  }
  const carousel = new Carousel();
  carousel.setSettings(carouselSettings);
  carousel.create();
};

onArticleCardCarouselReady$.subscribe(updateArticleCardCarousel);
onArticleCardCarouselSettingsReady$.subscribe(setArticleCardCarousel);

export default onArticleCardCarouselSettingsReady$;